<template>
  <form ref="password_form" @submit.prevent="updatePassword">
    <md-card>
      <md-card-header class="md-card-header-icon">
        <div class="card-icon">
          <md-icon>lock_reset</md-icon>
        </div>
        <h4 class="title">Reset Password</h4>
        <p class="category" v-if="isAdminReset">
          <md-icon>admin_panel_settings</md-icon>
          Admin Reset - No current password required. User will be forced to change on next login.
        </p>
      </md-card-header>

      <md-card-content>
        <div class="md-layout">
          <div class="md-layout-item md-size-100">
            <!-- Admin Options -->
            <div v-if="isAdmin" class="admin-options">
              <md-field>
                <md-checkbox v-model="forceChange">Force password change on next login</md-checkbox>
              </md-field>
              <md-field>
                <md-checkbox v-model="skipStrengthValidation">Skip strong password validation (not recommended)</md-checkbox>
              </md-field>
              <div v-if="skipStrengthValidation" class="warning-box">
                <md-icon>warning</md-icon>
                <span>Weak passwords are allowed. This is not recommended for security reasons.</span>
              </div>
            </div>

            <!-- New Password -->
            <md-field :class="{'md-error': apiValidationErrors.new_password || apiValidationErrors.password}">
              <label>{{ isAdminReset ? 'New Password' : 'New Password' }}</label>
              <md-input
                  v-model="password"
                  :type="showPassword ? 'text' : 'password'"
                  @input="onPasswordChange"
                  @focus="showRequirements = true"
                  @blur="showRequirements = false"
              />
              <md-button class="md-icon-button md-simple toggle-password" @click="showPassword = !showPassword" type="button">
                <md-icon>{{ showPassword ? 'visibility_off' : 'visibility' }}</md-icon>
              </md-button>
            </md-field>
            <validation-error :errors="apiValidationErrors.new_password || apiValidationErrors.password"/>

            <!-- Password Strength Meter -->
            <div v-if="password && password.length > 0" class="password-meter">
              <div class="meter-bar">
                <div
                    class="meter-fill"
                    :style="{
                    width: passwordScore + '%',
                    background: passwordStrengthColor
                  }"
                ></div>
              </div>
              <div class="meter-label">
                <span :style="{ color: passwordStrengthColor }">
                  <md-icon class="strength-icon">{{ passwordStrengthIcon }}</md-icon>
                  {{ passwordStrengthLabel }}
                </span>
                <span class="score">{{ passwordScore }}%</span>
              </div>
            </div>

            <!-- Password Requirements -->
            <div v-if="showRequirements || password" class="password-requirements">
              <h5>Password Requirements:</h5>
              <ul>
                <li v-for="req in requirementsList" :key="req.key" :class="{'met': req.met}">
                  <md-icon>{{ req.met ? 'check_circle' : 'radio_button_unchecked' }}</md-icon>
                  {{ req.label }}
                </li>
              </ul>
            </div>

            <!-- Confirm Password -->
            <md-field :class="{'md-error': apiValidationErrors.confirm_password || passwordMismatch}">
              <label>Confirm Password</label>
              <md-input
                  v-model="password_confirmation"
                  :type="showPassword ? 'text' : 'password'"
                  @input="checkPasswordMatch"
              />
              <span class="md-error" v-if="passwordMismatch">
                Passwords do not match
              </span>
            </md-field>
            <validation-error :errors="apiValidationErrors.confirm_password"/>

            <!-- Password History Warning -->
            <div v-if="passwordInHistory" class="history-warning">
              <md-icon>history</md-icon>
              <span>This password has been used recently. Please choose a different password.</span>
            </div>

            <!-- User Info -->
            <div v-if="user" class="user-info">
              <md-icon>person</md-icon>
              <span>Resetting password for: <strong>{{ user.name || user.email }}</strong></span>
              <span v-if="user.email" class="user-email">({{ user.email }})</span>
            </div>
          </div>
        </div>
      </md-card-content>

      <md-card-actions>
        <md-button type="submit" class="md-primary md-raised" :disabled="isSubmitting || !isFormValid">
          <md-icon>{{ isSubmitting ? 'hourglass_empty' : 'lock_reset' }}</md-icon>
          {{ isSubmitting ? 'Resetting...' : (isAdminReset ? 'Reset Password' : 'Change Password') }}
        </md-button>
        <md-button type="button" class="md-danger" @click="resetForm">
          <md-icon>clear</md-icon>
          Clear
        </md-button>
      </md-card-actions>
    </md-card>
  </form>
</template>

<script>
import {ValidationError} from '@/components';
import formMixin from '@/mixins/form-mixin';
import {mapActions} from "vuex";
import debounce from 'lodash/debounce';

export default {
  name: 'reset-password-card',

  props: {
    user: {
      type: Object,
      required: true
    },
    isAdmin: {
      type: Boolean,
      default: false
    },
    isAdminReset: {
      type: Boolean,
      default: false
    }
  },

  components: {ValidationError},

  mixins: [formMixin],

  data() {
    return {
      password: null,
      password_confirmation: null,
      forceChange: true,
      skipStrengthValidation: false,
      showPassword: false,
      showRequirements: false,
      isSubmitting: false,
      passwordMismatch: false,
      passwordInHistory: false,
      passwordScore: 0,
      passwordStrengthLabel: 'Weak',
      passwordStrengthColor: '#f44336',
      passwordStrengthIcon: 'error',
      requirements: {
        length: false,
        uppercase: false,
        lowercase: false,
        number: false,
        special: false,
        no_repeating: false,
        no_common_pattern: false
      }
    };
  },

  computed: {
    requirementsList() {
      return [
        { key: 'length', label: 'At least 8 characters long', met: this.requirements.length },
        { key: 'uppercase', label: 'Contains at least one uppercase letter', met: this.requirements.uppercase },
        { key: 'lowercase', label: 'Contains at least one lowercase letter', met: this.requirements.lowercase },
        { key: 'number', label: 'Contains at least one number', met: this.requirements.number },
        { key: 'special', label: 'Contains at least one special character', met: this.requirements.special },
        { key: 'no_repeating', label: 'No three or more repeating characters', met: this.requirements.no_repeating },
        { key: 'no_common_pattern', label: 'Not a common or easily guessable password', met: this.requirements.no_common_pattern }
      ];
    },

    isFormValid() {
      if (!this.password || this.password.length < 8) return false;
      if (!this.password_confirmation) return false;
      if (this.passwordMismatch) return false;
      if (this.passwordInHistory) return false;

      // If not skipping validation, require strong password
      if (!this.skipStrengthValidation && this.passwordScore < 80) return false;

      return true;
    }
  },

  watch: {
    'password': function() {
      this.passwordInHistory = false;
    }
  },

  methods: {
    ...mapActions('passwordModule', ['resetPassword', 'checkPasswordStrength', 'changePassword']),

    onPasswordChange: debounce(function() {
      this.checkPasswordStrengthDebounced();
    }, 300),

    async checkPasswordStrengthDebounced() {
      if (!this.password || this.password.length < 1) {
        return;
      }

      try {
        const response = await this.checkPasswordStrength({
          password: this.password
        });

        const data = response.data || response;
        this.passwordScore = data.score || 0;
        this.passwordStrengthLabel = data.strength ? data.strength.label : 'Weak';
        this.passwordStrengthColor = data.strength ? data.strength.color : '#f44336';
        this.passwordStrengthIcon = this.getStrengthIcon(this.passwordScore);
        this.requirements = data.requirements || {};
        this.passwordInHistory = false;
      } catch (error) {
        console.error('Password strength check failed:', error);
      }
    },

    getStrengthIcon(score) {
      if (score < 40) return 'error';
      if (score < 60) return 'warning';
      if (score < 80) return 'info';
      return 'check_circle';
    },

    checkPasswordMatch() {
      if (this.password_confirmation && this.password) {
        this.passwordMismatch = this.password !== this.password_confirmation;
      } else {
        this.passwordMismatch = false;
      }
    },

    async updatePassword() {
      this.isSubmitting = true;
      this.resetApiValidation();

      // Validate passwords match
      if (this.password !== this.password_confirmation) {
        this.passwordMismatch = true;
        this.$store.dispatch('alerts/error', 'Passwords do not match');
        this.isSubmitting = false;
        return;
      }

      // Validate password strength (if not skipping)
      if (!this.skipStrengthValidation && this.passwordScore < 80) {
        this.$store.dispatch('alerts/error', 'Please choose a stronger password');
        this.isSubmitting = false;
        return;
      }

      try {
        let payload = {
          id: this.user.id,
          new_password: this.password,
          confirm_password: this.password_confirmation,
        };

        // Add admin-specific fields
        if (this.isAdminReset || this.isAdmin) {
          payload.force_change = this.forceChange;
        }

        // Use resetPassword for admin, changePassword for regular users
        let response;
        if (this.isAdminReset || this.isAdmin) {
          response = await this.resetPassword(payload);
        } else {
          response = await this.changePassword(payload);
        }

        this.notifySuccess('Password updated successfully');
        this.resetForm();
        this.$emit('success', response);

      } catch (error) {
        console.error('Password update error:', error);

        if (error.response && error.response.data) {
          if (error.response.data.errors) {
            this.setApiValidation(error.response.data.errors);
          }
          if (error.response.data.message) {
            this.$store.dispatch('alerts/error', error.response.data.message);
          } else {
            this.$store.dispatch('alerts/error', 'Failed to update password');
          }
        } else {
          this.$store.dispatch('alerts/error', 'An unexpected error occurred');
        }
      } finally {
        this.isSubmitting = false;
      }
    },

    resetForm() {
      this.password = null;
      this.password_confirmation = null;
      this.passwordMismatch = false;
      this.passwordInHistory = false;
      this.passwordScore = 0;
      this.requirements = {
        length: false,
        uppercase: false,
        lowercase: false,
        number: false,
        special: false,
        no_repeating: false,
        no_common_pattern: false
      };
      this.resetApiValidation();
      this.$refs.password_form?this.$refs.password_form.reset():null;
    }
  }
};
</script>

<style scoped>
.md-card {
  margin-bottom: 20px;
}

.card-icon {
  display: flex;
  align-items: center;
  justify-content: center;
}

.category {
  font-size: 12px;
  color: #666;
  margin-top: 5px;
  display: flex;
  align-items: center;
  gap: 5px;
}

.category md-icon {
  font-size: 16px;
  width: 16px;
  height: 16px;
}

.admin-options {
  background: #f5f5f5;
  padding: 15px;
  border-radius: 6px;
  margin-bottom: 15px;
}

.admin-options .md-field {
  margin: 5px 0;
}

.warning-box {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 10px 15px;
  background: #fff3e0;
  border-radius: 4px;
  margin-top: 10px;
  color: #e65100;
  font-size: 13px;
}

.warning-box md-icon {
  color: #e65100;
}

.toggle-password {
  position: absolute;
  right: 0;
  top: 50%;
  transform: translateY(-50%);
}

.password-meter {
  margin: 10px 0 15px;
}

.meter-bar {
  width: 100%;
  height: 8px;
  background: #e0e0e0;
  border-radius: 4px;
  overflow: hidden;
}

.meter-fill {
  height: 100%;
  transition: width 0.3s ease;
}

.meter-label {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-top: 5px;
  font-size: 13px;
}

.meter-label .strength-icon {
  font-size: 18px;
  width: 18px;
  height: 18px;
  vertical-align: middle;
}

.score {
  color: #999;
  font-weight: 500;
}

.password-requirements {
  background: #f5f5f5;
  padding: 15px 20px;
  border-radius: 6px;
  margin: 10px 0 15px;
}

.password-requirements h5 {
  margin: 0 0 8px 0;
  font-size: 13px;
  color: #555;
}

.password-requirements ul {
  list-style: none;
  padding: 0;
  margin: 0;
}

.password-requirements li {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 3px 0;
  font-size: 12px;
  color: #999;
  transition: color 0.2s;
}

.password-requirements li.met {
  color: #4caf50;
}

.password-requirements li md-icon {
  font-size: 18px;
  width: 18px;
  height: 18px;
}

.history-warning {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 10px 15px;
  background: #fff3e0;
  border-radius: 4px;
  margin: 10px 0;
  color: #e65100;
  font-size: 13px;
}

.history-warning md-icon {
  color: #e65100;
}

.user-info {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 10px 15px;
  background: #e3f2fd;
  border-radius: 4px;
  margin-top: 15px;
  font-size: 13px;
  color: #1a237e;
}

.user-info md-icon {
  color: #1a237e;
}

.user-email {
  color: #666;
  font-size: 12px;
}

.md-card-actions {
  display: flex;
  gap: 10px;
  padding: 15px 20px;
  border-top: 1px solid #e0e0e0;
}

.md-card-actions .md-button {
  min-width: 120px;
}

/* Responsive */
@media (max-width: 768px) {
  .md-card-actions {
    flex-direction: column;
  }

  .md-card-actions .md-button {
    width: 100%;
  }

  .admin-options {
    padding: 10px;
  }
}
</style>
